<i18n>
{
  "fr": {
    "legend-title": "Légende",
    "groundwater-temp": "Température de l'eau",
    "avgValue": "Moyenne",
    "minValue": "Minimum",
    "maxValue": "Maximum",
    "value": "",
    "axis-range-default": "Par défaut",
    "axis-range-validated": "Données validées",
    "axis-range-auto": "Auto",
    "axis-range-y": "Limites axe Y (min / max)",
    "axis-range-info": {
      "default": "<b>Par défaut</b>: Limites de l'axe Y définies sur les bornes min et max de l'ensemble de la chronique de mesures.",
      "validated": "<b>Données validées</b>: Limites de l'axe Y définies sur les bornes min et max des données validées.",
      "auto": "<b>Auto</b>: Limites de l'axe Y définies sur les bornes min et max des données actuellement affichées dans le graphique."
    },
    "common": {
      "close": "Fermer"
    }
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="wrapper" v-if="show">
    <div class="graph-option">
      <md-field class="option axis-range" v-if="showAxisRangeSelect">
        <label @click="showAxisRangeInfo = true">{{ $t('axis-range-y') }} <md-icon class="label-icon clickable">help</md-icon></label>
        <md-select v-model="selectedAxisRangeMode" @md-selected="refreshGraph">
          <md-option value="default">{{ $t('axis-range-default') }}</md-option>
          <md-option value="validated">{{ $t('axis-range-validated') }}</md-option>
          <md-option value="auto">{{ $t('axis-range-auto') }}</md-option>
        </md-select>
      </md-field>
    </div>

    <div class="data-graph">
      <plotly
        v-if="showGraph"
        :data="graphData"
        :layout="graphLayout"
        @mouse-over="onMouseHover"
        @mouse-out="onMouseOut"
        :mouseOverValue="highlightedTimestamp"
        ref="plotly"
      />

      <div class="loading" v-if="loading">
        <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
      </div>
    </div>

    <md-dialog :md-active.sync="showAxisRangeInfo" class="axis-range-info">
      <md-dialog-content>
        <ul>
          <li v-html="$t('axis-range-info.default')"></li>
          <li v-html="$t('axis-range-info.validated')"></li>
          <li v-html="$t('axis-range-info.auto')"></li>
        </ul>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="showAxisRangeInfo = false">{{ $t('common.close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>

import _ from 'lodash'
import { timeFormat } from 'd3'

import CommonMixin from '@/mixins/store/common'
import TimeMixin from '@/mixins/store/time'
import { PlotlyData, PlotlyXAxis, PlotlyYAxis, PlotlyLayout } from '@/utils/plotly'
import GroundwaterGraphMixin from '@/mixins/graph/groundwaterGraph'

export default {
  name: 'GroundwaterTemp',
  mixins: [
    CommonMixin,
    TimeMixin,
    GroundwaterGraphMixin
  ],
  data() {
    return {
      color: '#a72d1a',
      showAxisRangeInfo: false
    }
  },
  computed: {
    graphLayout () {
      let toReturn = new PlotlyLayout()
      toReturn.xaxis = new PlotlyXAxis()
      toReturn.xaxis.isDate()
      toReturn.xaxis.tickformat = this.tickformat
      toReturn.xaxis.hoverformat = this._hoverformat
      toReturn.xaxis.tickangle = '0'
      toReturn.xaxis.range = [this._dateFrom, this._dateTo]
      toReturn.xaxis.fixedrange = this.fixedRange
      if (this.showTickRangeOnly) {
        toReturn.xaxis.tickvals = [this._dateFrom, this._dateTo]
        toReturn.xaxis.ticktext = [timeFormat(this.tickformat)(new Date(this._dateFrom)), timeFormat(this.tickformat)(new Date(this._dateTo))]
      }
      toReturn.xaxis.visible = this.isXaxisVisible

      toReturn.yaxis = new PlotlyYAxis(this.measuredTypeName('TEMP'))
      toReturn.yaxis.range = this.getAxisRange('TEMP')
      toReturn.yaxis.fixedrange = this.fixedRange

      toReturn.margin = this.margin

      return toReturn
    }
  },
  methods: {
    convertToGraphData (timeserie) {
      let color = this.getColor(timeserie.datasource)

      let xValues = timeserie.data.map((d) => this.formatMomentUtc(d.event))

      if (this._aggregationMode === 'raw') {
        let toReturn = new PlotlyData(timeserie.datasource.datasourceType.name[this.$i18n.locale], xValues, timeserie.data.map((d) => d.value), null, color)
        toReturn.showlegend = this.showLegends
        toReturn.line.width = 2

        // On n'affiche pas de légende s'il s'agit des données brutes d'une seule station
        if (this.timeseries.length === 1) toReturn.showlegend = false
        toReturn.hoverinfo = 'x+y'
        toReturn.hovertemplate = `%{y:.2f} <extra>${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}</extra>`
        return toReturn
      }

      // let min = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t('min')}`, xValues, timeserie.data.map((d) => d.minValue), null)
      // min.isZoneMin()
      // min.legendgroup = timeserie.datasource.datasourceType.code
      // min.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t('min')}</extra>`
      // min.showlegend = this.showLegends

      let avg = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t(this.selectedValue)}`, xValues, timeserie.data.map((d) => d[this.selectedValue]), null)
      // avg.isZoneMedian()
      avg.legendgroup = timeserie.datasource.datasourceType.code
      avg.line.color = color
      avg.line.width = 2
      avg.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t(this.selectedValue)}</extra>`
      avg.showlegend = this.showLegends

      // let max = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t('max')}`, xValues, timeserie.data.map((d) => d.maxValue), null)
      // max.isZoneMax()
      // max.legendgroup = timeserie.datasource.datasourceType.code
      // max.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t('max')}</extra>`
      // max.showlegend = this.showLegends

      return [
        // min,
        avg,
        // max
      ]
    },
    isDatasourceDisplayable (datasource) {
      // On ne prend en compte que les paramètres de températures (TEMP)
      return !datasource.datasourceType.webviewConfig.hidden &&
      datasource.parameterType.code === 'TEMP' &&
      _.includes(this.datasourceTypes, datasource.datasourceType.code) &&
      datasource.stats.minDate !== null
    },
    fetchTimeseries () {
      return new Promise((resolve,reject) => {
        this.timeseries = []
        let toFetch = []

        // Filtrer les sources de données à afficher:
        //  - Il ne faut pas de flag "hidden"
        // - Il faut que le paramètre soit de type 'TEMP'
        let datasources = this.datasources.filter((datasource) => this.isDatasourceDisplayable(datasource))
        datasources.forEach((datasource) => toFetch.push(this.getData(datasource.id)))

        Promise.all(toFetch).then((timeseries) => {
          timeseries.forEach((timeserie, index) => {
            this.timeseries.push({
              datasource: datasources[index],
              data: timeserie
            })
          })

          resolve()
        }).catch(reject)
      })
    },
    updateTimeline () {
      let events = []
      this.datasources.filter((ds) => { return this.isDatasourceDisplayable(ds) }).forEach((datasource) => events.push({
        id: datasource.id,
        timelineId: datasource.parameterType.id,
        timelineName: this.$t('groundwater-temp'),
        eventName: datasource.datasourceType.name[this.$i18n.locale],
        start: this.momentUtc(datasource.stats.minDate).utcOffset(0, true), // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
        end: this.momentUtc(datasource.stats.maxDate).utcOffset(0, true),   // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
        color: this.getColor(datasource), 
        order: datasource.parameterType.order
      }))
      this.addEvents(events)
    },
  }
}
</script>

<style lang="scss" scoped>
  .graph-option {
    display: flex;
    margin-left: 100px;
    margin-bottom: -30px;
    gap: 15px;

    .option {
      max-width: 140px;

      &.md-field {
        margin-bottom: 0px !important;
      }

      &.axis-range {
        max-width: 167px;
      }
    }
  }

  .data-graph {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .axis-range-info {
    .md-content {
      padding: 25px;
    }
    ul {
      padding-left: 15px;
    }
  }
</style>
